import cn from 'classnames';
import { forwardRef } from 'react';

import TableSectionContext from '../table/table-section-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { TableSectionContextProps } from '../table/table.types';
import type { TableBodyProps } from './table-body.types';

import './table-body.scss';

const contextValue: TableSectionContextProps = { variant: 'body' };

/**
 * The component renders a `<tbody>` element and it should contain `TableRow` components.
 *
 * ### Usage
 *
 * ```tsx
 * import { Table, TableBody, TableRow, TableCell } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomBody() {
 *   return (
 *     <TableBody className="custom-body">
 *       <TableRow>
 *         <TableCell>This cell will be rendered as `<td>`</TableCell>
 *       </TableRow>
 *    </TableBody>
 *   );
 * }
 * ```
 */
const TableBody = forwardRef(
  (props: TableBodyProps, forwardedRef: ForwardedRef<HTMLTableSectionElement> | null): ReactElement => {
    const {
      children,
      className,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    return (
      <TableSectionContext.Provider value={contextValue}>
        <tbody className={cn('rbui-table-body', className)} ref={forwardedRef} {...rest}>
          {children}
        </tbody>
      </TableSectionContext.Provider>
    );
  },
);

TableBody.displayName = 'TableBody';

export default TableBody;
